import { Box, Center, Flex } from '@chakra-ui/react';
import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  ChatEventType,
  PossibleEnrichments,
  useClient,
  useCurrentExpert,
  useIsFindingExpert,
  useMessage,
  useSessionInfo,
  useVendor
} from '@outdoorly/react-chat-sdk';
import { AnimatePresence } from 'framer-motion';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { INITIAL_MESSAGE_PAGE_SIZE } from '../../App';
import { Store } from '../../store';
import useArrowKeyNavigation from '../../utils/keyboard-nav';
import { ChatBox } from '../ChatBox/index';
import { IntroSequence } from '../IntroSequence';
import { Loading } from '../Loading';
import { WaitingForPairing } from '../WaitingForPairing';
import { AIPersonaIndicator } from './AIPersonaIndicator';
import { MessageGroup, useMessageGroups } from './messages';
import { TypingIndicator } from './TypingIndicator';
import { InChatPoweredByRemark } from '../PoweredByRemark';

type MessageListProps = {};
export type MessageAttributes = PossibleEnrichments & {
  censored?: string;
  completed?: boolean;
  skipped?: boolean;
  cancelled?: boolean;
  idleCloseTime?: string;
};
function iOS() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

export const MessageList: React.FC<MessageListProps> = React.memo(() => {
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const forcefullyShowFindingExpert = Store.useState((s) => s.forcefullyShowFindingExpert);

  const { sessionInfo: session, initialized } = useSessionInfo();

  const [waitForLastMessage, setWaitForLastMessage] = useState(false);
  const isActivatorVisible = Store.useState((s) => s.activatorVisible);
  const lastSeenMessageIndex = Store.useState((s) => s.lastSeenMessageIndex);

  const client = useClient();
  const endOfMessagesRef = useRef<null | HTMLDivElement>(null);
  const endOfMessageCallback = useCallback(
    (e: HTMLDivElement | null) => {
      // scroll to the bottom when list loads
      if (e && !endOfMessagesRef?.current) {
        endOfMessagesRef.current = e;
        setTimeout(() => e.scrollIntoView(), 10);
      }
    },
    [endOfMessagesRef]
  );
  const { messageList: messages } = useMessage();
  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;
    if (rootNode) {
      const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop;
      lastScrollDistanceToBottomRef.current = scrollDistanceToBottom;
    }
  }, []);
  const didAILeave = useMemo(() => {
    //loop backwards from join event to find the most recent leave or join event,
    //if AI left last, it's an AI transfer to human.
    const interruptingEvents = new Set<ChatEventType>([
      ChatEventType.Joined,
      ChatEventType.Left,
      ChatEventType.Closed,
      ChatEventType.Reopened,
      ChatEventType.Started
    ]);
    for (let index = messages.length - 1; index >= 0; index--) {
      const messageAttributes = messages[index]?.attributes as MessageAttributes;
      if (messageAttributes?.type && interruptingEvents.has(messageAttributes?.type)) {
        return false;
      }
      if (messageAttributes?.type === ChatEventType.AiLeft) {
        return true;
      }
    }
    return false;
  }, [messages]);
  const fetchMoreMessages = async () => {
    const scrollDiff =
      (scrollableRootRef?.current?.scrollHeight ?? 0) - (scrollableRootRef?.current?.scrollTop ?? 0);
    if (!loading && messages?.[0] && scrollDiff > 1000) {
      setLoading(true);
      const anchorIndex = messages[0].index - 1;

      if (anchorIndex < 0) {
        setHasMore(false);
        setLoading(false);
        return;
      }
      await client?.pageBack(INITIAL_MESSAGE_PAGE_SIZE, anchorIndex).then((result) => {
        setHasMore(result?.hasPrevPage ?? false);

        setTimeout(() => setLoading(false), 200);
      });
    }
  };
  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: loading || !endOfMessagesRef?.current,
    hasNextPage: hasMore,
    onLoadMore: () => fetchMoreMessages(),
    rootMargin: '200px 0px 0px 0px',
    delayInMs: iOS() ? 1000 : 100
  });
  useLayoutEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    const lastScrollDistanceToBottom = lastScrollDistanceToBottomRef.current ?? 0;

    if (scrollableRoot) {
      scrollableRoot.scrollTo({ top: scrollableRoot.scrollHeight - lastScrollDistanceToBottom });
    }
    const messageAttributes = messages[messages.length - 1]?.attributes as PossibleEnrichments;

    if (messages && messages.length && messageAttributes?.expert) {
      scrollToBottomIfClose();
      if (messageAttributes?.type === ChatEventType.Joined) {
        scrollToBottom();
      }
      if (isActivatorVisible && lastSeenMessageIndex < messages[messages.length - 1].index) {
        localStorage.setItem('remark-unseenRemarkMessages', String(Store.getRawState().unseenMessages + 1));
        // TODO, delete after 1/27/22, replace getItem instances
        localStorage.setItem('unseenRemarkMessages', String(Store.getRawState().unseenMessages + 1));
        localStorage.setItem('remark-lastSeenMessageIndex', String(messages[messages.length - 1].index));
        // TODO, delete after 1/27/22, replace getItem instances
        localStorage.setItem('lastSeenMessageIndex', String(messages[messages.length - 1].index));
        Store.update((s) => {
          s.unseenMessages += 1;
          s.lastSeenMessageIndex = messages[messages.length - 1].index;
        });
      }
    } else if (messages && messages.length && !messageAttributes?.expert) {
      scrollToBottomIfClose();
    }
  }, [messages, rootRef]);

  const findingExpert = useIsFindingExpert();

  const { initialized: expertInitialized, currentExpert } = useCurrentExpert();

  const isAIExpert = !!currentExpert?.isAiExpert;
  const gridRef = useArrowKeyNavigation({ selectors: '[role="gridcell"]' });

  const { vendor } = useVendor();

  const scrollableRootRef = React.useRef<HTMLDivElement | null>(null);
  const lastScrollDistanceToBottomRef = React.useRef<number>();
  const scrollToBottom = () => {
    const scrollElement = scrollableRootRef?.current;
    scrollElement?.scrollTo(0, scrollElement?.scrollHeight);
    setTimeout(() => scrollElement?.scrollTo(0, scrollElement?.scrollHeight), 200);
  };
  const scrollToBottomIfClose = (scrollDiffAmount?: number) => {
    const scrollElement = scrollableRootRef?.current;
    const scrollDiff = (scrollElement?.scrollHeight ?? 0) - (scrollElement?.scrollTop ?? 0);
    if (scrollDiff < (scrollDiffAmount ?? 1200)) {
      scrollToBottom();
    }
  };
  const rootRefSetter = useCallback(
    (node: HTMLDivElement) => {
      rootRef(node);
      scrollableRootRef.current = node;
      scrollToBottom();
    },
    [rootRef]
  );

  const groupedMessages = useMessageGroups(messages);

  return (
    <Flex
      flexDir="column"
      h={'calc(100% - 66px)'}
      maxH={'calc(100% - 66px)'}
      justify="space-between"
      as="main"
      key="messageListContent"
    >
      <AnimatePresence>
        {!initialized && (
          <Center h={'calc(100% - 66px)'} maxH={'calc(100% - 66px)'} key="loadingMessages" pos="relative">
            <Loading />
          </Center>
        )}
      </AnimatePresence>
      {!initialized ? null : findingExpert || forcefullyShowFindingExpert ? (
        <WaitingForPairing
          key="waitingForPairing"
          scrollToBottomIfClose={scrollToBottomIfClose}
          didAILeave={didAILeave}
        />
      ) : (
        <Fragment key="messagesFragment">
          <Flex
            direction="column"
            key="messageListContainer"
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              overflowAnchor: 'none'
            }}
            h="100%"
            p="16px 8px 8px 8px"
            overflowY="auto"
            overflowX="hidden"
            flexGrow={1}
            pointerEvents="auto"
            m={0}
            ref={rootRefSetter}
            onScroll={handleRootScroll}
            scrollBehavior="auto"
            id="remark-widget-message-list"
            marginInlineStart={0}
            overscrollBehavior="contain"
          >
            <div ref={infiniteRef} />
            {isAIExpert && <AIPersonaIndicator expertFirstName={currentExpert.publicName} />}
            <Center key="loading-spin" flex={1}>
              <AnimatePresence>
                {(loading || hasMore) &&
                  !waitForLastMessage &&
                  !session?.canStartNewChat &&
                  messages &&
                  messages.length >= INITIAL_MESSAGE_PAGE_SIZE && <Loading key="loadingMoreSpinner" />}
              </AnimatePresence>
            </Center>
            <Box
              mt="8px"
              role="grid"
              tabIndex={-1}
              ref={gridRef as any}
              display="flex"
              flexDir="column"
              justifyContent="flex-end"
              justifySelf="flex-end"
            >
              {!waitForLastMessage && (
                <Fragment>
                  {groupedMessages.map((messageGroup, index, allGroups) => {
                    const nextMessageGroup = allGroups.at(index + 1);
                    return (
                      messageGroup.length > 0 && (
                        <MessageGroup
                          key={messageGroup.at(0)?.sid}
                          messageGroup={messageGroup}
                          vendorLogo={vendor?.logo}
                          isLastGroup={index === allGroups.length - 1}
                          nextGroupIsSameAuthor={
                            messageGroup.at(-1)?.author === nextMessageGroup?.at(0)?.author
                          }
                          scrollToBottomIfClose={scrollToBottomIfClose}
                          eventIsPiiCollectionFlow={
                            (messageGroup.at(-1)?.attributes as MessageAttributes | undefined)?.type ===
                              ChatEventType.NoExpertsAvailable &&
                            (nextMessageGroup?.at(0)?.attributes as MessageAttributes | undefined)?.type ===
                              ChatEventType.PiiRequest
                          }
                        />
                      )
                    );
                  })}
                </Fragment>
              )}
              {(waitForLastMessage || session?.canStartNewChat) && (
                <IntroSequence
                  key="introSequence"
                  showWaitingForExpert={(show) => {
                    Store.update((s) => {
                      s.forcefullyShowFindingExpert = show;
                    });
                  }}
                  showWaitForLastMessage={(show) => {
                    setWaitForLastMessage(show);
                  }}
                  scrollToBottom={scrollToBottom}
                />
              )}
            </Box>
            {!waitForLastMessage && (
              <TypingIndicator
                avatar={currentExpert?.avatar ? currentExpert.avatar : vendor?.logo ?? ''}
                key="typingIndicator"
                scrollToBottom={scrollToBottomIfClose}
              />
            )}
            {messages && messages?.length > 0 && (
              <Box
                id="bottom-of-message-list"
                ref={endOfMessageCallback}
                h="3px"
                key={`eom-${messages.at(-1)?.sid}`}
                minH="3px"
                w="100%"
                pos="relative"
              />
            )}
            <InChatPoweredByRemark />
          </Flex>
          {currentExpert && expertInitialized && (
            <ChatBox
              key="chatBox"
              scrollToBottom={scrollToBottom}
              scrollToBottomIfClose={scrollToBottomIfClose}
            />
          )}
        </Fragment>
      )}
    </Flex>
  );
});
